import React, { useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../contexts/AuthContext';

export const PluginDiagnostic: React.FC = () => {
  const { isAdmin, user } = useAuth();
  const [results, setResults] = useState<string[]>([]);
  const [testing, setTesting] = useState(false);

  const log = (message: string) => {
    setResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const runDiagnostics = async () => {
    setTesting(true);
    setResults([]);
    
    try {
      log('Starting plugin management diagnostics...');
      
      // Test 1: Check admin status
      log(`Admin status: ${isAdmin ? 'Yes' : 'No'}`);
      log(`User ID: ${user?.id || 'None'}`);
      
      // Test 2: Check if plugins table exists and is accessible
      log('Testing plugins table access...');
      try {
        const { data: pluginsData, error: pluginsError } = await supabase
          .from('plugins')
          .select('*', { count: 'exact', head: true });
        
        if (pluginsError) {
          log(`❌ Plugins table error: ${pluginsError.message}`);
        } else {
          log('✅ Plugins table accessible');
        }
      } catch (err) {
        log(`❌ Plugins table exception: ${err}`);
      }
      
      // Test 3: Check if plugin_versions table exists and is accessible
      log('Testing plugin_versions table access...');
      try {
        const { data: versionsData, error: versionsError } = await supabase
          .from('plugin_versions')
          .select('*', { count: 'exact', head: true });
        
        if (versionsError) {
          log(`❌ Plugin_versions table error: ${versionsError.message}`);
        } else {
          log('✅ Plugin_versions table accessible');
        }
      } catch (err) {
        log(`❌ Plugin_versions table exception: ${err}`);
      }
      
      // Test 4: Check storage bucket access
      log('Testing storage bucket access...');
      try {
        const { data: buckets, error: bucketsError } = await supabase.storage.listBuckets();
        
        if (bucketsError) {
          log(`❌ Storage bucket error: ${bucketsError.message}`);
        } else {
          log(`✅ Storage API accessible. Found ${buckets?.length || 0} buckets`);
          if (buckets && buckets.length > 0) {
            buckets.forEach(bucket => {
              log(`  - Bucket: ${bucket.id} (${bucket.name}) - Public: ${bucket.public}`);
            });
          }
          
          const pluginBucket = buckets?.find(b => b.id === 'plugin-files');
          if (pluginBucket) {
            log('✅ Plugin-files bucket exists');
            
            // Test if we can list files in the bucket
            const { data: files, error: filesError } = await supabase.storage
              .from('plugin-files')
              .list('', { limit: 1 });
            
            if (filesError) {
              log(`❌ Cannot access plugin-files bucket: ${filesError.message}`);
            } else {
              log('✅ Plugin-files bucket is accessible');
            }
          } else {
            log('❌ Plugin-files bucket not found');
          }
        }
      } catch (err) {
        log(`❌ Storage bucket exception: ${err}`);
      }
      
      // Test 5: Test plugin creation permissions
      if (isAdmin) {
        log('Testing plugin creation permissions...');
        try {
          const testPlugin = {
            id: 'test-plugin-diagnostic',
            name: 'Test Plugin Diagnostic',
            description: 'Test plugin for diagnostic purposes',
            author: 'EasyDevelopment',
            enabled: false
          };
          
          const { error: insertError } = await supabase
            .from('plugins')
            .insert(testPlugin);
          
          if (insertError) {
            log(`❌ Plugin creation error: ${insertError.message}`);
          } else {
            log('✅ Plugin creation successful');
            
            // Clean up test plugin
            await supabase
              .from('plugins')
              .delete()
              .eq('id', 'test-plugin-diagnostic');
            log('✅ Test plugin cleaned up');
          }
        } catch (err) {
          log(`❌ Plugin creation exception: ${err}`);
        }
      }
      
      // Test 6: Check user profile and admin role
      log('Checking user profile and roles...');
      try {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('role, is_admin')
          .eq('id', user?.id)
          .single();
        
        if (profileError) {
          // Try without is_admin column in case it doesn't exist
          const { data: profileAlt, error: profileErrorAlt } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', user?.id)
            .single();
          
          if (profileErrorAlt) {
            log(`❌ Profile check error: ${profileErrorAlt.message}`);
          } else {
            log(`✅ User role: ${profileAlt?.role || 'None'}`);
            log(`⚠️ is_admin column missing - run the fix script`);
          }
        } else {
          log(`✅ User role: ${profile?.role || 'None'}`);
          log(`✅ Is admin flag: ${profile?.is_admin ? 'Yes' : 'No'}`);
        }
      } catch (err) {
        log(`❌ Profile check exception: ${err}`);
      }
      
      log('Diagnostics completed!');
      
    } catch (error) {
      log(`❌ Diagnostic error: ${error}`);
    } finally {
      setTesting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-lg font-semibold text-red-800 mb-2">Access Denied</h3>
        <p className="text-red-600">This diagnostic tool is only available to administrators.</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-xl font-bold mb-4">Plugin Management Diagnostics</h2>
      
      <button
        onClick={runDiagnostics}
        disabled={testing}
        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 mb-4"
      >
        {testing ? 'Running Diagnostics...' : 'Run Diagnostics'}
      </button>
      
      {results.length > 0 && (
        <div className="mt-4">
          <h3 className="text-lg font-semibold mb-2">Results:</h3>
          <div className="bg-gray-100 p-4 rounded max-h-96 overflow-y-auto">
            {results.map((result, index) => (
              <div
                key={index}
                className={`text-sm font-mono mb-1 ${
                  result.includes('❌') ? 'text-red-600' :
                  result.includes('✅') ? 'text-green-600' :
                  'text-gray-700'
                }`}
              >
                {result}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}; 